import {
  Card,
  CardBody,
  Center,
  Container,
  Divider,
  HStack,
  Stack,
  Text,
  useColorMode,
} from "@chakra-ui/react";
import { QueryClient, dehydrate, useQuery } from "@tanstack/react-query";
import { useRef, useState } from "react";

import CardAboutModal from "@/components/CardAboutModal";
import CardDelisted from "@/components/CardDelisted";
import CheckOutWrapper from "@/components/stripe/CheckOutWrapper";
import { CloseIcon } from "@chakra-ui/icons";
import DiscoverIconSM from "@/components/DiscoverIconSm";
import Head from "next/head";
import Link from "next/link";
import Loading from "@/components/Loading";
import PlayBox from "@/components/PlayBox";
import { Songcard } from "@/types";
import ThisIsModal from "@/components/ThisIsModal";
import Thumbnail from "@/components/Thumbnails";
import { apiUrl } from "@/constants";
import { useInView } from "framer-motion";
import { useRouter } from "next/router";

interface CardProps {
  card: Songcard;
}

export default function CardPage() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const { colorMode } = useColorMode();
  const router = useRouter();
  const [modelOpen, setModalOpen] = useState(false);
  const { uuid } = router.query;
  const [showCheckout, setShowCheckout] = useState<boolean>(false);

  const getCard = async () => {
    const res = await fetch(`${apiUrl}/ninja/cards/${uuid}`);
    const data: Songcard = await res.json();
    return data;
  };

  const {
    data: card,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["card", uuid],
    queryFn: getCard,
    enabled: !!uuid,
  });

  if (isLoading) return <Loading />;

  if (isError) return <CardDelisted />;

  return (
    <>
      {card && card.uuid && card.delisted && (
        <>
          <CardDelisted />
        </>
      )}
      {card && !card.uuid && (
        <>
          <CardDelisted />
        </>
      )}
      {card && card.uuid && !card.delisted && (
        <div>
          <Head>
            <title>{`${card.primary_artist?.name ?? ""} - ${
              card.title
            } | Songcards`}</title>
            <meta property="og:site_name" content="Songcards" />
            <meta
              itemProp="image"
              content={`${card.asset ?? card.comp_file ?? card.ext_file ?? ""}`}
            />
            <link
              rel="canonical"
              href={`https://songcards.io/cards/${card.uuid}`}
            />
            <meta name="description" content="Listen. Discover. Collect." />
            <meta
              name="viewport"
              content="width=device-width, initial-scale=1"
            />
            <link rel="apple-touch-icon" href="/icon.png" />
            <link rel="icon" href="/songcards.png" />

            <meta
              property="og:image"
              content={`${card.asset ?? card.comp_file ?? card.ext_file ?? ""}`}
            />
            <meta property="og:image:width" content="400" />
            <meta property="og:image:height" content="300" />
            <meta
              property="og:url"
              content={`https://songcards.io/cards/${card.uuid}`}
            />
            <meta property="og:type" content="website" />
            <meta property="og:site_name" content="Songcards" />
            <meta
              property="og:title"
              content={`${card.primary_artist?.name ?? ""} - ${
                card.title
              } | Songcards`}
            />
            <meta
              property="og:description"
              content="Listen. Discover. Collect."
            />
            <meta name="twitter:domain" content="songcards.io" />
            <meta name="twitter:site" content="@unlockyoursound" />
            <meta name="twitter:card" content="summary_large_image" />
            <meta
              name="twitter:title"
              content={`${card.primary_artist?.name ?? ""} - ${
                card.title
              } | Songcards`}
            />
            <meta
              name="twitter:description"
              content="Listen. Discover. Collect."
            />

            <meta
              name="twitter:image"
              content={`${card.asset ?? card.comp_file ?? card.ext_file ?? ""}`}
            />
            <meta name="apple-itunes-app" content="app-id=6737206374" />
          </Head>
          <Center
            ref={ref}
            style={{
              transform: isInView ? "none" : "translateY(+100px)",
              transition: "all 0.9s cubic-bezier(0.17, 0.55, 0.55, 1) 0.5s",
            }}
          >
            <Card
              maxW="xs"
              align="center"
              bgColor={colorMode === "dark" ? "black" : "white"}
              variant="outline"
              borderColor={colorMode === "dark" ? "white" : "black"}
              borderStyle="dotted"
              borderRadius={15}
              shadow={"md"}
            >
              <CardBody>
                <div onClick={() => setModalOpen(true)}>
                  <Thumbnail
                    ext_file={card.ext_file}
                    comp_file={card.comp_file}
                    image={card.image}
                  />
                </div>
                <Center>
                  <Stack mt={5} mb="0" spacing="3">
                    <PlayBox card={card} />
                    <Text ml={4} mb={1} mt={2}>
                      is a Songcard from
                      <br />
                      {card.primary_artist && (
                        <Text as="b" key={card.primary_artist.slug}>
                          <Link href={`/artists/${card.primary_artist.slug}`}>
                            {card.primary_artist.name}
                          </Link>
                        </Text>
                      )}
                    </Text>
                    {!showCheckout && (
                      <>
                        <Divider mb={1} />
                        <Center>
                          <HStack justify="space-between" w="100%">
                            <>
                              <CardAboutModal
                                card={card}
                                price={false}
                                showOpen={true}
                              />
                              <ThisIsModal
                                card={card}
                                price={false}
                                showOpen={modelOpen}
                                setModalOpen={setModalOpen}
                              />
                            </>
                          </HStack>
                        </Center>
                      </>
                    )}
                    {showCheckout && (
                      <>
                        <CheckOutWrapper uuid={card.uuid} id={card.id} />
                        <Center>
                          <CloseIcon
                            onClick={() => setShowCheckout(false)}
                            cursor={"pointer"}
                          />
                        </Center>
                      </>
                    )}
                  </Stack>
                </Center>
              </CardBody>
            </Card>
          </Center>
          <DiscoverIconSM />
          <Container mb={200}>
            <></>
          </Container>
        </div>
      )}
    </>
  );
}

interface ParamProps {
  params: {
    uuid: string;
  };
}

export async function getStaticProps({ params }: ParamProps) {
  const { uuid } = params;
  const queryClient = new QueryClient();

  const getCard = async () => {
    const res = await fetch(`${apiUrl}/ninja/cards/${uuid}`);
    const data: Songcard = await res.json();
    return data;
  };

  await queryClient.prefetchQuery({
    queryKey: ["card", uuid],
    queryFn: getCard,
  });

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
    },
    revalidate: 86400,
  };
}

export async function getStaticPaths(): Promise<
  | {
      paths: string[];
      fallback: string;
    }
  | undefined
> {
  const endpoint = `${apiUrl}/ninja/cards`;

  if (endpoint) {
    const response = await fetch(endpoint);
    const cards = await response.json();

    const paths = await cards.map((card: CardProps["card"]) => ({
      params: { uuid: card.uuid },
    }));

    return { paths, fallback: "blocking" };
  } else {
    return undefined;
  }
}
